import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, Switch } from "@mui/material";
import { Link } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

function ExamLists() {
  const [items, setItems] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({});
  const [notification, setNotification] = useState({ open: false, color: "success", message: "" });
  const [togglingStatusIds, setTogglingStatusIds] = useState({});

  const showMessage = (message, color = "success") => {
    setNotification({ open: true, color, message });
    setTimeout(() => setNotification({ open: false, color, message: "" }), 2500);
  };

  const loadItems = async (page = pageNo, currentLimit = limit) => {
    if (init() !== "success") return;

    try {
      const resp = await axios.get(
        `${Constants.getUrls.getAllExamLists}?page=${page}&limit=${currentLimit}`
      );
      setItems(resp?.data?.data?.docs || []);
      setPagination(resp?.data?.data || {});
      setPageNo(resp?.data?.data?.page || page);
      setLimit(resp?.data?.data?.limit || currentLimit);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to load exam lists.", "error");
    }
  };

  useEffect(() => {
    loadItems(1, limit);
  }, []);

  const handleDelete = async (id) => {
    try {
      const resp = await axios.delete(`${Constants.deleteUrls.deleteExamList}${id}`);
      showMessage(resp?.data?.message || "Exam list deleted successfully.");
      loadItems(pageNo, limit);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to delete exam list.", "error");
    }
  };

  const handleStatusToggle = async (item) => {
    if (!item?._id) return;

    const nextStatus = item?.status === "active" ? "inactive" : "active";
    setTogglingStatusIds((prev) => ({ ...prev, [item._id]: true }));

    try {
      const resp = await axios.post(`${Constants.putUrls.updateExamList}${item._id}`, {
        status: nextStatus,
      });
      showMessage(resp?.data?.message || "Exam list status updated successfully.");
      loadItems(pageNo, limit);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to update exam list status.", "error");
    } finally {
      setTogglingStatusIds((prev) => ({ ...prev, [item._id]: false }));
    }
  };

  const rows = items.map((item, index) => ({
    serial: (pageNo - 1) * limit + index + 1,
    exam: item?.exam_id?.exam_name || "-",
    year: item?.year_id?.year || "-",
    name: item?.exam_name || "-",
    questions: item?.number_of_questions ?? 0,
    shift: item?.shift || "-",
    cutoff: item?.cutoff || "-",
    status: (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Switch
          checked={item?.status === "active"}
          disabled={Boolean(togglingStatusIds[item._id])}
          onChange={() => handleStatusToggle(item)}
          color="success"
        />
        <span
          style={{
            color: item?.status === "active" ? "#16a34a" : "#f59e0b",
            fontWeight: 700,
            textTransform: "capitalize",
          }}
        >
          {item?.status === "active" ? "active" : "inactive"}
        </span>
      </MDBox>
    ),
    action: (
      <MDBox display="flex" gap={1}>
        <Link to={`/update-exam-list/${item._id}`}>
          <MDButton variant="outlined" size="small" color="info">
            Update
          </MDButton>
        </Link>
        <MDButton variant="outlined" size="small" color="error" onClick={() => handleDelete(item._id)}>
          Delete
        </MDButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
            <MDTypography variant="h6" color="white">
              Exam Lists
            </MDTypography>
          </MDBox>
          <Grid item sm={4} px={2} py={2} align="right">
            <Link to="/add-exam-list">
              <MDButton variant="contained" size="small" color="primary">
                Create Exam List
              </MDButton>
            </Link>
          </Grid>
          <MDBox>
            <DataTable
              table={{
                columns: [
                  { Header: "serial", accessor: "serial", width: "6%" },
                  { Header: "Exam", accessor: "exam" },
                  { Header: "Year", accessor: "year" },
                  { Header: "Name", accessor: "name" },
                  { Header: "Questions", accessor: "questions" },
                  { Header: "Shift", accessor: "shift" },
                  { Header: "Cutoff", accessor: "cutoff" },
                  { Header: "Status", accessor: "status" },
                  { Header: "Action", accessor: "action" },
                ],
                rows,
              }}
              entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
              page_no={pagination?.page}
              totalPages={pagination?.totalPages}
              hasNextPage={pagination?.hasNextPage}
              hasPrevPage={pagination?.hasPrevPage}
              isnextPage={pagination?.nextPage}
              prevPage={pagination?.prevPage}
              totalDocs={pagination?.totalDocs}
              onPageChange={(page) => loadItems(page, limit)}
              onEntriesPerPageChange={(newLimit) => loadItems(1, newLimit)}
            />
          </MDBox>
          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default ExamLists;
